// ReportModal — the actual "Report" flow behind the Report buttons on streams,
// profiles and posts. Backend (/reports, /staff/reports, /staff/reports/{id}/action)
// already existed and worked; nothing in the UI ever called it, so users had no way
// to file a report despite the Community Guidelines promising one on every
// stream/post/profile. This is that missing piece.
import { useState } from "react";
import { X, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const REASONS = [
  { value: "nudity", label: "Nudity or sexual content" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate", label: "Hate speech or discrimination" },
  { value: "violence", label: "Violence or threats" },
  { value: "spam", label: "Spam or scam" },
  { value: "minor_safety", label: "Involves a minor unsafely" },
  { value: "other", label: "Something else" },
];

export default function ReportModal({ targetType, targetId, onClose }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setBusy(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details: details || null });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="report-modal">
      <div
        className="w-full sm:max-w-md bg-white text-nexus-obsidian rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <div className="text-xs tracking-[0.28em] uppercase font-display text-nexus-obsidian flex items-center gap-2">
            <Flag className="w-3.5 h-3.5 text-nexus-crimson" /> Report {targetType}
          </div>
          <button onClick={onClose} className="text-black/50 hover:text-black" data-testid="report-close" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="p-6 text-center space-y-2" data-testid="report-done">
            <div className="text-sm font-medium">Report submitted</div>
            <div className="text-xs text-black/50">Our team reviews reports within 24 hours. Thanks for helping keep NEXUS safe.</div>
            <button onClick={onClose} className="btn-nexus mt-2 !inline-flex" data-testid="report-done-close">Close</button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div>
              <label className="text-[10px] tracking-widest uppercase text-black/50">Reason</label>
              <div className="mt-1.5 space-y-1.5">
                {REASONS.map((r) => (
                  <label key={r.value} className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm ${reason === r.value ? "border-nexus-crimson bg-nexus-crimson/5" : "border-black/10 hover:bg-black/5"}`}>
                    <input type="radio" name="report-reason" checked={reason === r.value} onChange={() => setReason(r.value)} data-testid={`report-reason-${r.value}`} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-widest uppercase text-black/50">Details (optional)</label>
              <textarea
                value={details} onChange={(e) => setDetails(e.target.value)} maxLength={500} rows={3}
                className="mt-1.5 w-full border border-black/15 rounded-md px-3 py-2 text-sm"
                placeholder="Anything that helps us review this faster"
                data-testid="report-details"
              />
            </div>
            <button
              onClick={submit}
              disabled={busy || !reason}
              className="w-full rounded-md px-4 py-2.5 text-sm font-medium text-white bg-nexus-crimson disabled:opacity-40"
              data-testid="report-submit"
            >
              {busy ? "Submitting…" : "Submit report"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
